package com.siriusxm.pia.views.unifiedaggregator.backfill

import com.siriusxm.unifiedcontent.console.aggregator.intakeErrorMessage
import com.siriusxm.unifiedcontent.console.aggregator.rememberCapabilities
import androidx.compose.runtime.*
import com.siriusxm.cmc.error
import com.siriusxm.cmc.utils.encodeURIComponent
import com.siriusxm.cmc.utils.toLocalDateTimeString
import com.siriusxm.cmc.utils.toShortDateTimeString
import com.siriusxm.pia.Application
import com.siriusxm.pia.SXMUI
import com.siriusxm.pia.components.*
import com.siriusxm.pia.views.unifiedaggregator.AggregatorService
import com.siriusxm.pia.views.unifiedaggregator.arnToConsoleUrl
import unifiedcontent.aggregator.BackfillStatus
import unifiedcontent.aggregator.TypeProgress
import kotlinx.coroutines.delay
import kotlinx.coroutines.isActive
import org.jetbrains.compose.web.attributes.ATarget
import org.jetbrains.compose.web.attributes.target
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Text
import smithy4kt.SmithyError
import kotlin.time.Duration.Companion.seconds


fun Number.toLocaleString(): String {
    return this.asDynamic()?.toLocaleString()
}

fun BackfillStatus.isRunning(): Boolean {
    return status == "ACTIVE" || status == "CREATING"
}

/**
 * Render the backfill page.
 */
@Composable
fun ServiceViewContext.backfill(
    aggregatorService: AggregatorService,
    id: String,
    backfillStatus: BackfillStatus? = null
) {
    var status by remember { mutableStateOf(backfillStatus) }
    var error by remember { mutableStateOf<SmithyError?>(null) }
    var targetName by remember { mutableStateOf<String?>(null) }
    var jobId by remember { mutableStateOf<String?>(null) }

    val baseUrl = Application.configuration.datadogLookupByJobId

    LaunchedEffect(id) {
        if (status == null) {
            try {
                status = aggregatorService.clientApi.getBackfillJob(id)
                targetName = status?.targetArn?.substringAfterLast("/")
                jobId = status?.jobId
            } catch (e: SmithyError) {
                error = e
            }
        }

        // Periodically fetch the status of the job
        while (isActive && status?.isRunning() == true) {
            status = aggregatorService.clientApi.getBackfillJob(id)
            delay(1.seconds)
        }
    }

    suspend fun cancel() {
        try {
            aggregatorService.clientApi.cancelBackfill(id)
        } catch (t: Throwable) {
            t.printStackTrace()
            val failure = intakeErrorMessage("cancel this backfill", t)
            aggregatorService.notifications.error(failure.message, failure.description)
        }
    }

    val jobTarget = status?.targetArn
    val jobProbe = remember(jobTarget) { jobTarget?.let { backfillProbe(it) } }
    val jobDecisions by aggregatorService.context.rememberCapabilities(listOfNotNull(jobProbe))
    // One Backfill grant covers cancelling a job and running it again.
    val canOperate = jobProbe?.let { jobDecisions?.get(it) } == true

    serviceContentView({
        title = "Backfill Job"
        subTitle = targetName
        breadcrumbs {
            crumb("Aggregator", "aggregator") {
                crumb("Backfills", "backfill") {
                    crumb(id, null)
                }
            }
        }

        // Hide-only: shown when AVP allows the operation against this job's target. The aggregator
        // re-checks against the stored job, so a stale ALLOW still ends in a surfaced 403/503.
        if (status != null && canOperate) {
            if (status?.status == "ACTIVE") {
                action("Cancel") {
                    cancel()
                }
            } else if (status?.status != "ACTIVE") {
                action("Run Again") {
                    val role = status?.roleArn?.let { encodeURIComponent(it) }
                    val target = status?.targetArn?.let { encodeURIComponent(it) }
                    val types = status?.typeProgress?.keys?.mapNotNull { type ->
                        aggregatorService.entityTypes.find { it.type == type }
                    }?.joinToString(",") { it.type }?.let { encodeURIComponent(it) }.orEmpty()
                    if (target != null && types.isNotBlank())
                        aggregatorService.navigate("backfill/new?target=$target${role?.let { "&role=$it" } ?: ""}&types=${types}")
                }
            }
        }
    }) {
        if (status == null) {
            spinner(size = Size.LARGE)
        } else {
            // Boxes own no outer margin; this container owns the gap between the
            // detail box and the Types table.
            Column(gap = 20.px) {
            box {
                var resolvedStatus = status?.status ?: "STARTING"
                if (status?.status == "ACTIVE") {
                    val allComplete = status?.typeProgress?.values.orEmpty().find { !it.isComplete } == null
                    if (allComplete) {
                        resolvedStatus = "FINISHING"
                    }
                }

                detailGrid {
                    detail("Status", resolvedStatus)
                    detail("Entity Count", status?.count?.toLocaleString())
                    detail("Job ID", status?.jobId)
                    // Render timestamps in the globally-selected timezone (header picker), not
                    // the browser's system zone. Project the instant into the chosen IANA zone
                    // first, then format the resulting LocalDateTime.
                    detail("Started", status?.startTime?.toLocalDateTimeIn(currentTimeZoneId())?.toShortDateTimeString())
                    status?.completeTime?.let {
                        detail("Completed", it.toLocalDateTimeIn(currentTimeZoneId()).toShortDateTimeString())
                    }
                    detail("Data Timestamp", status?.timestamp?.toLocalDateTimeIn(currentTimeZoneId())?.toShortDateTimeString())

                    if (status?.filter?.startTs != null || status?.filter?.endTs != null) {
                        detail("Filter") {
                            Column {
                                status?.filter?.startTs?.let { start ->
                                    Row {
                                        Text("Changes since: ${start.toLocalDateTimeIn(currentTimeZoneId()).toShortDateTimeString()}")
                                    }
                                }
                                status?.filter?.endTs?.let { start ->
                                    Row {
                                        Text("Changes before: ${start.toLocalDateTimeIn(currentTimeZoneId()).toShortDateTimeString()}")
                                    }
                                }
                            }
                        }
                    }

                    detail("Target") {
                        val targetArn = status?.targetArn.orEmpty()
                        val consoleUrl = arnToConsoleUrl(targetArn)
                        if (consoleUrl != null) {
                            A(href = consoleUrl, {
                                target(ATarget.Blank)
                            }) {
                                Text(targetArn)
                            }
                        } else {
                            Text(targetArn)
                        }
                    }
                    status?.roleArn?.takeIf { it.isNotBlank() }?.let { roleArn ->
                        detail("Role") {
                            A(href = "https://us-east-1.console.aws.amazon.com/iam/home?region=us-east-2#/roles/details/${
                                roleArn.substringAfterLast("role/")
                            }",
                                {
                                    target(ATarget.Blank)
                                }) {
                                Text(roleArn)
                            }
                        }
                    }
                    detail("Logs") {
                        if (jobId != null) {
                            val url = baseUrl.replace("{{JOB_ID}}", jobId!!)
                            A(href = url, {
                                target(ATarget.Blank)
                            }) {
                                Img(src= "images/dd-logo-rgb.png", "DataDog") {
                                    style {
                                        width(20.px)
                                        property("vertical-align", "middle")
                                        marginRight(3.px)
                                    }
                                }
                                Text("DataDog")
                            }
                        } else {
                            Text("Job ID is unavailable")
                        }

                    }
                }
            }

            boxTable<Pair<String, TypeProgress>>("Types") {
                items(status?.typeProgress?.map {
                    it.key to it.value
                })

                column {
                    width = 20.em
                    content {
                        val typeString = aggregatorService.entityType(it.first)?.name ?: it.first
                        Text(typeString)
                    }
                }
                column {
                    width = 15.em
                    content {
                        Text(it.second.count.toLocaleString())
                    }
                }
                column {
                    content { typeStatus ->
                        if (status?.status == "ACTIVE") {
                            Div({
                                style {
                                    width(200.px)
                                    height(20.px)
                                    backgroundColor(SXMUI.borderDividerDefault.value())
                                    borderRadius(5.px)
                                    overflow("hidden")
                                }
                            }) {
                                val index = typeStatus.second.currentSegment
                                val total = typeStatus.second.totalSegments
                                val progress = if (total <= 0) 0 else {
                                    ((index.toDouble() / total.toDouble()) * 100).toInt()
                                }
                                Div({
                                    style {
                                        display(DisplayStyle.InlineBlock)
                                        height(100.percent)
                                        width(progress.percent)
                                        backgroundColor(SXMUI.goColor.value())
                                    }
                                }) {

                                }
                            }
                        }
                    }
                }
            }
            }
        }
    }
}
